import FormattedFileSize from "#Components/formatters/FormattedFileSize/FormattedFileSize"
import FormattedHumanNumber from "#Components/formatters/FormattedHumanNumber"
import { useGraphqlQuery } from "#React/hooks/useGraphqlQuery"
import HoverCard from "#React/ui/HoverCard/HoverCard"

const USAGE_QUERY = gql`
  query GetOrganizationUsage($slug: String!) {
    organization(slug: $slug) {
      id
      onFreePlan
      freePlanLimitDescription
      daysLeftInTrial
      billingCycleUsage {
        apmUsage
        apmLimit
        logUsage
        logLimit
        daysLeft
      }
    }
  }
`

const CircularProgress = ({ percentage = 0 }) => {
  const radius = 6
  const strokeWidth = 3
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (percentage / 100) * circumference

  return (
    <svg className="transform -rotate-90 size-4" viewBox="0 0 16 16">
      <circle
        cx="8"
        cy="8"
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        className="stroke-blue-200"
      />

      <circle
        cx="8"
        cy="8"
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        className="stroke-blue-500"
      />
    </svg>
  )
}

CircularProgress.propTypes = {
  percentage: PropTypes.number,
  size: PropTypes.number,
}

const UsageBreakdown = ({ usageData }) => {
  const toPercentage = (value, total) => {
    if (value === 0) return 0

    return Math.min(100, Math.max(0, Math.round((value / total) * 100)))
  }

  return (
    <div className="w-48">
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-content-default font-medium">APM Usage</span>
            <span className="text-content-secondary text-sm">
              <FormattedHumanNumber value={usageData.apmUsage} precision={0} />
              {" / "}
              <FormattedHumanNumber value={usageData.apmLimit} precision={0} />
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-1">
            <div
              className="bg-blue-500 h-1 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${toPercentage(usageData.apmUsage, usageData.apmLimit)}%` }}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-content-default font-medium">Log Usage</span>
            <span className="text-content-secondary text-sm">
              <FormattedFileSize value={usageData.logUsage} format="byte" precision={0} />
              {" / "}
              <FormattedFileSize value={usageData.logLimit} format="byte" precision={0} />
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-1">
            <div
              className="bg-blue-500 h-1 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${toPercentage(usageData.logUsage, usageData.logLimit)}%` }}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-content-default font-medium">Month progress</span>
            <span className="text-content-secondary text-sm">{31 - usageData.daysLeft} / 31</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-1">
            <div
              className="bg-blue-500 h-1 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${toPercentage(31 - usageData.daysLeft, 31)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

UsageBreakdown.propTypes = {
  usageData: PropTypes.shape({
    apmUsage: PropTypes.number,
    apmLimit: PropTypes.number,
    logUsage: PropTypes.number,
    logLimit: PropTypes.number,
    daysLeft: PropTypes.number,
  }).isRequired,
}

const FreePlanQuotaNotification = ({ organizationSlug, enabled = false }) => {
  const { data } = useGraphqlQuery(USAGE_QUERY, {
    variables: {
      slug: organizationSlug,
    },
  })

  enabled = (data?.organization?.onFreePlan && data?.organization?.daysLeftInTrial <= 0) || enabled

  if (!enabled) return null

  const usageData = data?.organization?.billingCycleUsage

  const getPercentageUsed = (data) => {
    if (!data) return 0

    const apmPercentage = (data.apmUsage / data.apmLimit) * 100
    const logsPercentage = (data.logUsage / data.logLimit) * 100

    return Math.min(100, Math.max(0, Math.max(apmPercentage, logsPercentage)))
  }

  return (
    <HoverCard>
      <HoverCard.Trigger
        closeDelay={0}
        render={
          <div className="bg-surface-highlight text-link rounded-sm p-3">
            <div className="flex items-center">
              <div className="flex-none">
                <CircularProgress percentage={getPercentageUsed(usageData)} />
              </div>
              <div className="flex-auto responsive-hidden">
                <span className="font-bold mr-1">Free plan</span>
              </div>
              <a
                href={`/${organizationSlug}/admin/plans/new`}
                className="c-link text-xs responsive-hidden"
              >
                Upgrade
              </a>
            </div>
          </div>
        }
      ></HoverCard.Trigger>
      {usageData && (
        <HoverCard.Content theme="light" className="border border-edge-default rounded-md">
          <UsageBreakdown usageData={usageData} />
          {data?.organization?.freePlanLimitDescription && (
            <p className="mt-3 max-w-64 text-sm text-content-secondary">
              {data.organization.freePlanLimitDescription}
            </p>
          )}
        </HoverCard.Content>
      )}
    </HoverCard>
  )
}

FreePlanQuotaNotification.propTypes = {
  organizationSlug: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
}

FreePlanQuotaNotification.propTypes = {
  organizationSlug: PropTypes.string.isRequired,
}

export default FreePlanQuotaNotification
